/**
 * CustomToolsPage Component
 * Dedicated Agent Studio screen for managing external tools, webhooks, OpenAPI imports,
 * response filtering policies, and HITL execution controls.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check,
  Copy,
  Layers,
  Play,
  Plus,
  Search,
  Shield,
  Sparkles,
  Trash2,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { CustomToolModal } from '../components/tools/CustomToolModal';
import { OpenApiImportModal } from '../components/tools/OpenApiImportModal';
import { useAgents } from '../contexts/AgentsContext';
import { useNotification } from '../hooks/useNotification';
import {
  deleteToolDefinition,
  getToolPolicies,
  listToolDefinitions,
  updateToolDefinition,
} from '../services/api';
import { ToolDefinition, ToolPolicies, ToolResponsePolicy } from '../types';

export const CustomToolsPage: React.FC = () => {
  const { t } = useTranslation();
  const { agents } = useAgents();
  const { success: showSuccess, error: showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters State
  const initialAgentFilter = searchParams.get('agentId') || 'ALL';
  const [selectedAgentId, setSelectedAgentId] = useState<string>(initialAgentFilter);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [riskFilter, setRiskFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Data State
  const [tools, setTools] = useState<ToolDefinition[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [policies, setPolicies] = useState<ToolPolicies>({
    allowDeleteMethod: false,
  });

  // Modal States
  const [isToolModalOpen, setIsToolModalOpen] = useState<boolean>(false);
  const [isOpenApiModalOpen, setIsOpenApiModalOpen] = useState<boolean>(false);
  const [editingTool, setEditingTool] = useState<ToolDefinition | null>(null);
  const [toolToDelete, setToolToDelete] = useState<ToolDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Load Policies & Tools
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [toolsRes, policyRes] = await Promise.all([
        listToolDefinitions(
          selectedAgentId === 'ALL' ? undefined : selectedAgentId,
          statusFilter === 'ALL' ? undefined : statusFilter
        ),
        getToolPolicies(),
      ]);

      if (toolsRes.success && toolsRes.data) {
        setTools(toolsRes.data);
      } else {
        showError(toolsRes.error || t('tools.errors.loadToolsFailed', 'Failed to load tools'));
      }

      if (policyRes.success && policyRes.data) {
        setPolicies(policyRes.data);
      }
    } catch (err: any) {
      showError(err.message || t('tools.errors.loadToolsError', 'Error loading tools'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [selectedAgentId, statusFilter]);

  // Update URL params when agent filter changes
  const handleAgentFilterChange = (agentId: string) => {
    setSelectedAgentId(agentId);
    if (agentId === 'ALL') {
      searchParams.delete('agentId');
    } else {
      searchParams.set('agentId', agentId);
    }
    setSearchParams(searchParams);
  };

  // Toggle tool status
  const handleToggleStatus = async (tool: ToolDefinition) => {
    const newStatus = tool.status === 'active' ? 'disabled' : 'active';
    setTogglingId(tool.publicId);

    try {
      const res = await updateToolDefinition(tool.publicId, { status: newStatus });
      if (res.success && res.data) {
        setTools((prev) =>
          prev.map((t) => (t.publicId === tool.publicId ? { ...t, status: newStatus } : t))
        );
        const statusText =
          newStatus === 'active'
            ? t('tools.status.active', 'active')
            : t('tools.status.disabled', 'disabled');
        showSuccess(
          t('tools.messages.statusUpdated', {
            name: tool.name,
            status: statusText,
            defaultValue: `Tool "${tool.name}" is now ${statusText}`,
          })
        );
      } else {
        showError(res.error || t('tools.errors.updateStatusFailed', 'Failed to update tool status'));
      }
    } catch (err: any) {
      showError(err.message || t('tools.errors.toggleStatusFailed', 'Failed to toggle status'));
    } finally {
      setTogglingId(null);
    }
  };

  // Delete tool
  const handleConfirmDelete = async () => {
    if (!toolToDelete) return;
    setIsDeleting(true);

    try {
      const res = await deleteToolDefinition(toolToDelete.publicId);
      if (res.success) {
        setTools((prev) => prev.filter((t) => t.publicId !== toolToDelete.publicId));
        showSuccess(
          t('tools.messages.deleteSuccess', {
            name: toolToDelete.name,
            defaultValue: `Tool "${toolToDelete.name}" deleted successfully`,
          })
        );
        setToolToDelete(null);
      } else {
        showError(res.error || t('tools.errors.deleteFailed', 'Failed to delete tool'));
      }
    } catch (err: any) {
      showError(err.message || t('tools.errors.deleteFailed', 'Failed to delete tool'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy helper
  const handleCopyEndpoint = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter tools client-side for search and risk
  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      if (riskFilter !== 'ALL' && tool.riskLevel !== riskFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          tool.name.toLowerCase().includes(q) ||
          (tool.description || '').toLowerCase().includes(q) ||
          (tool.endpointUrl || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tools, riskFilter, searchQuery]);

  // Find agent name for a tool
  const getAgentName = (agentPublicId?: string | null) => {
    if (!agentPublicId) return t('tools.agent.shared', 'Shared / Global');
    const match = agents.find((a) => a.publicId === agentPublicId);
    return match ? match.name : t('tools.agent.unknown', 'Unknown Agent');
  };

  return (
    <Box className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header & Main Actions */}
      <Box className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <Box>
          <Box className="flex items-center gap-2.5">
            <Box className="p-2 rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400">
              <Wrench size={26} />
            </Box>
            <div>
              <Typography variant="h5" className="font-bold text-gray-900 dark:text-slate-100">
                {t('tools.title', 'Custom Tools & Webhooks')}
              </Typography>
              <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                {t(
                  'tools.subtitle',
                  'Connect your agents to external APIs with secure token redaction, response policies, and human confirmation.'
                )}
              </Typography>
            </div>
          </Box>
        </Box>

        <Box className="flex items-center gap-3">
          <Button
            variant="outlined"
            onClick={() => setIsOpenApiModalOpen(true)}
            startIcon={<Layers size={18} />}
            className="border-gray-300 text-gray-700 dark:border-slate-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            {t('tools.actions.importOpenApi', 'Import OpenAPI')}
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setEditingTool(null);
              setIsToolModalOpen(true);
            }}
            startIcon={<Plus size={18} />}
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {t('tools.actions.newTool', 'Create Tool')}
          </Button>
        </Box>
      </Box>

      {/* Security & Platform Policy Alerts */}
      {!policies.allowDeleteMethod && (
        <Alert
          severity="info"
          icon={<Shield size={18} />}
          className="rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/50 dark:bg-blue-950/20 text-xs"
        >
          <strong>{t('tools.policy.noticeTitle', 'Platform Policy Notice:')}</strong>{' '}
          {t(
            'tools.policy.deleteRestrictedNotice',
            'HTTP DELETE operations are currently restricted by global system policy. Only safe Read and Write methods (GET, POST, PUT, PATCH) can be registered.'
          )}
        </Alert>
      )}

      {/* Filter Toolbar */}
      <Card className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardContent className="p-4">
          <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search Input */}
            <TextField
              size="small"
              placeholder={t('tools.filters.searchPlaceholder', 'Search tools by name, description, or URL...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} className="text-gray-400" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Agent Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel id="filter-agent-label">{t('tools.filters.filterByAgent', 'Filter by Agent')}</InputLabel>
              <Select
                labelId="filter-agent-label"
                value={selectedAgentId}
                label={t('tools.filters.filterByAgent', 'Filter by Agent')}
                onChange={(e) => handleAgentFilterChange(e.target.value)}
              >
                <MenuItem value="ALL">{t('tools.filters.allAgentsAndGlobal', 'All Agents & Global')}</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.publicId} value={agent.publicId}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel id="filter-status-label">{t('tools.filters.status', 'Status')}</InputLabel>
              <Select
                labelId="filter-status-label"
                value={statusFilter}
                label={t('tools.filters.status', 'Status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">{t('tools.filters.allStatuses', 'All Statuses')}</MenuItem>
                <MenuItem value="active">{t('tools.filters.activeOnly', 'Active Only')}</MenuItem>
                <MenuItem value="disabled">{t('tools.filters.disabledOnly', 'Disabled Only')}</MenuItem>
              </Select>
            </FormControl>

            {/* Risk Level Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel id="filter-risk-label">{t('tools.filters.riskLevel', 'Risk Level')}</InputLabel>
              <Select
                labelId="filter-risk-label"
                value={riskFilter}
                label={t('tools.filters.riskLevel', 'Risk Level')}
                onChange={(e) => setRiskFilter(e.target.value)}
              >
                <MenuItem value="ALL">{t('tools.filters.allRiskLevels', 'All Risk Levels')}</MenuItem>
                <MenuItem value="read">{t('tools.filters.riskRead', 'Read (Safe)')}</MenuItem>
                <MenuItem value="write">{t('tools.filters.riskWrite', 'Write (Mutations)')}</MenuItem>
                <MenuItem value="sensitive">{t('tools.filters.riskSensitive', 'Sensitive (High Privileges)')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Tools List */}
      {isLoading ? (
        <Box className="flex flex-col items-center justify-center py-16 gap-3">
          <CircularProgress size={36} className="text-indigo-600" />
          <Typography variant="body2" className="text-gray-500">
            {t('tools.list.loading', 'Loading tool definitions...')}
          </Typography>
        </Box>
      ) : filteredTools.length === 0 ? (
        <Card className="rounded-xl border border-dashed border-gray-300 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50 p-12 text-center">
          <Box className="w-14 h-14 mx-auto rounded-full bg-indigo-50 dark:bg-indigo-950 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
            <Wrench size={28} />
          </Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            {t('tools.list.emptyTitle', 'No custom tools found')}
          </Typography>
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400 max-w-md mx-auto mt-1 mb-6">
            {t(
              'tools.list.emptyDescription',
              "Extend your agent's capabilities by registering custom API endpoints, or import an entire OpenAPI / Swagger spec."
            )}
          </Typography>
          <Box className="flex justify-center gap-3">
            <Button
              variant="outlined"
              onClick={() => setIsOpenApiModalOpen(true)}
              startIcon={<Layers size={16} />}
              className="border-gray-300 text-gray-700 dark:border-slate-700 dark:text-slate-300"
            >
              {t('tools.actions.importOpenApi', 'Import OpenAPI')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setEditingTool(null);
                setIsToolModalOpen(true);
              }}
              startIcon={<Plus size={16} />}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {t('tools.actions.newTool', 'Create Tool')}
            </Button>
          </Box>
        </Card>
      ) : (
        <Box className="space-y-3">
          {filteredTools.map((tool) => {
            const isToggling = togglingId === tool.publicId;

            return (
              <Card
                key={tool.publicId}
                variant="outlined"
                className={`rounded-xl border transition-all duration-200 ${
                  tool.status === 'active'
                    ? 'border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 dark:hover:border-slate-700'
                    : 'border-gray-200/60 dark:border-slate-800/60 bg-gray-50/60 dark:bg-slate-900/40 opacity-75'
                }`}
              >
                <CardContent className="p-4 sm:p-5">
                  <Box className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Left: Info & Badges */}
                    <Box className="space-y-2 flex-1 min-w-0">
                      <Box className="flex flex-wrap items-center gap-2">
                        {/* Method Badge */}
                        <Chip
                          label={tool.method}
                          size="small"
                          className={`font-mono font-bold text-xs ${
                            tool.method === 'GET'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                              : tool.method === 'POST'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                              : tool.method === 'PUT' || tool.method === 'PATCH'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                          }`}
                        />

                        {/* Tool Name */}
                        <Typography
                          variant="subtitle1"
                          className="font-mono font-bold text-gray-900 dark:text-slate-100 truncate"
                        >
                          {tool.name}
                        </Typography>

                        {/* Risk Level Badge */}
                        <Chip
                          label={
                            tool.riskLevel === 'read'
                              ? t('tools.riskLevels.read', 'Read')
                              : tool.riskLevel === 'write'
                              ? t('tools.riskLevels.write', 'Write')
                              : t('tools.riskLevels.sensitive', 'Sensitive')
                          }
                          size="small"
                          className={`text-xs capitalize ${
                            tool.riskLevel === 'read'
                              ? 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
                              : tool.riskLevel === 'write'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                          }`}
                        />

                        {/* HITL Mode Badge */}
                        <Chip
                          label={
                            tool.hitlMode === 'required'
                              ? t('tools.hitl.requiredBadge', 'HITL: Required')
                              : t('tools.hitl.bypassBadge', 'HITL: Bypass Allowed')
                          }
                          size="small"
                          variant="outlined"
                          className={`text-xs ${
                            tool.hitlMode === 'required'
                              ? 'border-indigo-300 text-indigo-700 dark:border-indigo-800 dark:text-indigo-400'
                              : 'border-gray-300 text-gray-600 dark:border-slate-700 dark:text-slate-400'
                          }`}
                        />

                        {/* Agent Badge */}
                        <Chip
                          label={getAgentName(tool.agentPublicId)}
                          size="small"
                          variant="outlined"
                          className="text-xs border-dashed border-gray-300 dark:border-slate-700 text-gray-500 dark:text-slate-400"
                        />
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" className="text-gray-600 dark:text-slate-300 line-clamp-2">
                        {tool.description || t('tools.list.noDescription', 'No description provided.')}
                      </Typography>

                      {/* Endpoint & Response Policy Summary */}
                      <Box className="flex flex-wrap items-center gap-3 pt-1 text-xs text-gray-500 dark:text-slate-400">
                        <Box className="flex items-center gap-1 font-mono bg-gray-100 dark:bg-slate-800/80 px-2 py-0.5 rounded max-w-md truncate">
                          <span className="truncate">{tool.endpointUrl}</span>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyEndpoint(tool.endpointUrl, tool.publicId)}
                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                          >
                            {copiedId === tool.publicId ? (
                              <Check size={12} className="text-emerald-500" />
                            ) : (
                              <Copy size={12} />
                            )}
                          </IconButton>
                        </Box>

                        {tool.responsePolicy && (
                          <Box className="flex items-center gap-1.5">
                            <Sparkles size={13} className="text-indigo-500" />
                            <span>
                              {t('tools.list.responsePolicySummary', {
                                paths:
                                  (tool.responsePolicy as ToolResponsePolicy).allowedJsonPaths?.length ||
                                  t('tools.list.allPaths', 'all'),
                                items: (tool.responsePolicy as ToolResponsePolicy).maxItems || 20,
                                defaultValue: `Response Policy: ${(tool.responsePolicy as ToolResponsePolicy).allowedJsonPaths?.length || 'all'} paths, max ${(tool.responsePolicy as ToolResponsePolicy).maxItems || 20} items`,
                              })}
                            </span>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Right: Actions & Toggle */}
                    <Box className="flex items-center gap-3 self-end lg:self-center">
                      {/* Active/Disabled Switch */}
                      <Box className="flex items-center gap-1.5 pr-2 border-r border-gray-200 dark:border-slate-800">
                        <Typography variant="caption" className="text-gray-500 text-xs font-medium capitalize">
                          {tool.status === 'active'
                            ? t('tools.status.active', 'active')
                            : t('tools.status.disabled', 'disabled')}
                        </Typography>
                        <Switch
                          size="small"
                          checked={tool.status === 'active'}
                          disabled={isToggling}
                          onChange={() => handleToggleStatus(tool)}
                        />
                      </Box>

                      {/* Test Connection Button */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEditingTool(tool);
                          setIsToolModalOpen(true);
                        }}
                        startIcon={<Play size={14} />}
                        className="text-xs border-gray-300 text-gray-700 dark:border-slate-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                      >
                        {t('tools.actions.testOrEdit', 'Test / Edit')}
                      </Button>

                      {/* Delete Action */}
                      <Tooltip title={t('tools.actions.deleteTool', 'Delete Tool')}>
                        <IconButton
                          size="small"
                          onClick={() => setToolToDelete(tool)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Manual Create / Edit Custom Tool Modal */}
      {isToolModalOpen && (
        <CustomToolModal
          open={isToolModalOpen}
          tool={editingTool}
          agents={agents}
          initialAgentId={selectedAgentId === 'ALL' ? undefined : selectedAgentId}
          onClose={() => {
            setIsToolModalOpen(false);
            setEditingTool(null);
          }}
          onSaved={() => {
            void loadData();
          }}
        />
      )}

      {/* OpenAPI Batch Import Modal */}
      {isOpenApiModalOpen && (
        <OpenApiImportModal
          open={isOpenApiModalOpen}
          agents={agents}
          initialAgentId={selectedAgentId === 'ALL' ? undefined : selectedAgentId}
          onClose={() => setIsOpenApiModalOpen(false)}
          onImportSuccess={(count) => {
            showSuccess(
              t('tools.messages.importSuccess', {
                count,
                defaultValue: `Successfully imported ${count} tools from OpenAPI`,
              })
            );
            void loadData();
          }}
        />
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={Boolean(toolToDelete)}
        title={t('tools.confirmDelete.title', 'Delete Custom Tool')}
        message={t('tools.confirmDelete.message', {
          name: toolToDelete?.name,
          defaultValue: `Are you sure you want to delete the tool "${toolToDelete?.name}"? Any agent workflows calling this tool will fail.`,
        })}
        confirmText={t('tools.confirmDelete.confirm', 'Delete Tool')}
        cancelText={t('tools.confirmDelete.cancel', 'Cancel')}
        loading={isDeleting}
        onClose={() => setToolToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default CustomToolsPage;
